/*
 * Theme controller — OS preference is the default; an explicit user choice
 * overrides it and persists. Theming itself lives entirely in the semantic
 * token tier (tokens.semantic.css [data-theme="dark"]); this module only sets
 * the `data-theme` attribute on <html>.
 *
 * `localStorage` here holds a UI preference and nothing else. Authentication
 * tokens live in MSAL's own cache and are NEVER written here (ADR-055 §4).
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "aic-admin-theme";

/** The user's stored override, or null when they've never chosen. */
export function getStoredTheme(): Theme | null {
  const value = localStorage.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" ? value : null;
}

/** The OS-level preference, defaulting to light when unknown. */
export function getSystemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** The theme in effect: an explicit override if present, else the OS preference. */
export function getActiveTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

/** Apply a theme to the document and persist it as an explicit override. */
export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem(STORAGE_KEY, theme);
}

/** Apply the active theme without persisting — call once on app start. */
export function initTheme(): void {
  document.documentElement.setAttribute("data-theme", getActiveTheme());
}
